// contextUtils.js
/**
 * Utilities for managing conversation context sizes and token estimation
 */

// Define token limits based on models
const MODEL_TOKEN_LIMITS = {
  "claude-3-7-sonnet-20250219": 200000, // Added Claude 3.7 Sonnet
  "claude-3-opus-20240229": 200000,
  "claude-3-sonnet-20240229": 200000,
  "claude-3-haiku-20240307": 200000,
  "claude-2.1": 100000,
};

// Context level multipliers
const CONTEXT_LEVEL_MULTIPLIERS = {
  "maximum": 1.0,
  "standard": 0.5,
  "minimum": 0.2
};

// Approximate tokens per message for system overhead
const SYSTEM_OVERHEAD_TOKENS = 100;

// Reserve tokens for Claude's response
const RESERVED_RESPONSE_TOKENS = 1024;

/**
 * Estimate the number of tokens in a text
 * Using a simple heuristic of ~4 characters per token for English text
 * 
 * @param {string} text - The text to estimate
 * @param {boolean} isCode - Whether the text is code (uses different ratio)
 * @returns {number} - Estimated token count
 */
export const estimateTokenCount = (text, isCode = false) => {
  if (!text) return 0;
  
  // Code is generally more token efficient
  const charsPerToken = isCode ? 5 : 4;
  
  return Math.ceil(text.length / charsPerToken);
};

/**
 * Detect if text is likely code based on markdown code block markers or high 
 * frequency of special characters
 * 
 * @param {string} text - The text to analyze
 * @returns {boolean} - Whether the text is likely code
 */
const isLikelyCode = (text) => {
  if (!text) return false;
  
  // Check for markdown code block
  if (text.includes("```")) return true;
  
  // Check for high frequency of special characters typical in code
  const codeCharacters = "{}[]()<>=-+*/:;.,!&#%^|~`$";
  let codeCharCount = 0;
  
  for (let i = 0; i < text.length; i++) {
    if (codeCharacters.includes(text[i])) {
      codeCharCount++;
    }
  }
  
  // If more than 10% of characters are code-like, likely code
  return (codeCharCount / text.length) > 0.1;
};

/**
 * Estimate tokens for a message object
 * 
 * @param {Object} message - Message object with role and content
 * @returns {number} - Estimated token count
 */
export const estimateMessageTokens = (message) => {
  if (!message || !message.content) return 0;
  
  const isCode = isLikelyCode(message.content);
  return estimateTokenCount(message.content, isCode) + 4; // +4 for role overhead
};

/**
 * Select messages to include based on context level
 * 
 * @param {Array} messages - Array of message objects
 * @param {string} model - Model ID 
 * @param {string} contextLevel - Context level (maximum, standard, minimum)
 * @returns {Array} - Selected messages to include
 */
export const selectMessagesForContext = (messages, model, contextLevel = "maximum") => {
  if (!messages || messages.length === 0) return [];
  
  // Get model token limit and apply context level multiplier
  const modelLimit = MODEL_TOKEN_LIMITS[model] || 100000;
  const multiplier = CONTEXT_LEVEL_MULTIPLIERS[contextLevel] || 1.0;
  
  // Calculate available tokens
  const availableTokens = Math.floor(modelLimit * multiplier) - SYSTEM_OVERHEAD_TOKENS - RESERVED_RESPONSE_TOKENS;
  
  // If we have very few messages, just return them all
  const totalTokens = messages.reduce((sum, msg) => sum + estimateMessageTokens(msg), 0);
  if (totalTokens <= availableTokens) {
    return messages;
  }
  
  // Always include the first message (often contains important context/instructions)
  const firstMessage = messages[0];
  const firstMessageTokens = estimateMessageTokens(firstMessage);
  
  // Current message is the last one in the array
  const currentMessageTokens = estimateMessageTokens(messages[messages.length - 1]);
  
  // Remaining tokens after including first and current message
  let remainingTokens = availableTokens - firstMessageTokens - currentMessageTokens;
  
  // If not enough tokens for even these, prioritize the current message
  if (remainingTokens < 0) {
    return [messages[messages.length - 1]];
  }
  
  // Create an array with first message and current message
  const selectedMessages = [firstMessage];
  
  // Fill in with most recent messages, working backwards from the end
  // (excluding the current message which we already counted)
  let currentTokens = firstMessageTokens;
  
  // Start from the second most recent message (second to last)
  for (let i = messages.length - 2; i > 0; i--) {
    const messageTokens = estimateMessageTokens(messages[i]);
    
    if (currentTokens + messageTokens <= availableTokens - currentMessageTokens) {
      selectedMessages.unshift(messages[i]); // Add at beginning to maintain order
      currentTokens += messageTokens;
    } else {
      // Don't add partial messages
      break;
    }
  }
  
  // Add the current message at the end
  selectedMessages.push(messages[messages.length - 1]);
  
  return selectedMessages;
};

/**
 * Check if the conversation is approaching the context limit
 * 
 * @param {Array} messages - Array of message objects
 * @param {string} model - Model ID
 * @param {string} contextLevel - Context level (maximum, standard, minimum)
 * @returns {boolean} - Whether the conversation is approaching limit
 */
export const isApproachingContextLimit = (messages, model, contextLevel = "maximum") => {
  if (!messages || messages.length === 0) return false;
  
  // Get model token limit and apply context level multiplier
  const modelLimit = MODEL_TOKEN_LIMITS[model] || 100000;
  const multiplier = CONTEXT_LEVEL_MULTIPLIERS[contextLevel] || 1.0;
  const availableTokens = Math.floor(modelLimit * multiplier) - SYSTEM_OVERHEAD_TOKENS - RESERVED_RESPONSE_TOKENS;
  
  // Calculate current usage
  const currentTokens = messages.reduce((sum, msg) => sum + estimateMessageTokens(msg), 0);
  
  // Return true if using more than 80% of available tokens
  return currentTokens > (availableTokens * 0.8);
};

/**
 * Get the current context utilization percentage
 * 
 * @param {Array} messages - Array of message objects
 * @param {string} model - Model ID
 * @param {string} contextLevel - Context level
 * @returns {number} - Percentage of context used (0-100)
 */
export const getContextUtilizationPercentage = (messages, model, contextLevel = "maximum") => {
  if (!messages || messages.length === 0) return 0;
  
  // Get model token limit and apply context level multiplier
  const modelLimit = MODEL_TOKEN_LIMITS[model] || 100000;
  const multiplier = CONTEXT_LEVEL_MULTIPLIERS[contextLevel] || 1.0;
  const availableTokens = Math.floor(modelLimit * multiplier) - SYSTEM_OVERHEAD_TOKENS - RESERVED_RESPONSE_TOKENS;
  
  // Calculate current usage
  const currentTokens = messages.reduce((sum, msg) => sum + estimateMessageTokens(msg), 0);
  
  // Return percentage
  return Math.min(100, Math.floor((currentTokens / availableTokens) * 100));
};

export default {
  estimateTokenCount,
  estimateMessageTokens,
  selectMessagesForContext,
  isApproachingContextLimit,
  getContextUtilizationPercentage,
  MODEL_TOKEN_LIMITS,
  CONTEXT_LEVEL_MULTIPLIERS
};